import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  type ThemingProps,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash/debounce";
import truncate from "lodash/truncate";
import React from "react";
import { useTranslation } from "react-i18next";
import type { onErrorCb } from "@/api/types";
import { InputStyled, SpinnerStyled } from "@/components/atoms";
import { ChevronDownIcon, LoopIcon } from "@/components/icons";
import { useGetAvailableSpace } from "@/lib/hooks";

type ItemType = { id: string | number; displayId?: string | number };

type FetchFuncParams = {
  search?: string;
  onError?: onErrorCb;
};

interface Props<T extends ItemType> {
  title: string;
  selectedItems: T["id"][];
  fetchItems: (params: FetchFuncParams) => Promise<T[]>;
  displayFieldKey: keyof T;
  fetchOnOpen?: boolean;
  showDisplayId?: boolean;
  defaultValue?: T["id"][];
  onChange: (items: T["id"][]) => void;
  variant?: ThemingProps<"Popover">["variant"];
  labelFullWidth?: boolean;
  isError?: boolean;
  isDisabled?: boolean;
  btnBorderColor?: string;
  alwaysShowResetBtn?: boolean;
}

export const DropdownMultiAsync = <T extends ItemType>(props: Props<T>) => {
  const { fetchOnOpen = true } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [allItems, setAllItems] = React.useState<T[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const selectedItemsRef = React.useRef<T[]>([]);
  const initialFocusRef = React.useRef<HTMLInputElement>(null);

  const { onOpen, onClose, isOpen } = useDisclosure();

  const displayedItems = React.useMemo(() => {
    const selectedSet = new Set(props.selectedItems);

    selectedItemsRef.current = selectedItemsRef.current
      .filter((item) => selectedSet.has(item.id))
      .map((selectedItem) => {
        const fullItem = allItems.find((item) => item.id === selectedItem.id);
        if (fullItem) {
          return fullItem;
        }
        return selectedItem;
      });

    if (selectedItemsRef.current.length < props.selectedItems.length) {
      selectedItemsRef.current = selectedItemsRef.current.concat(
        props.selectedItems.map((item) => ({ id: item, [props.displayFieldKey]: item }) as T),
      );
    }

    const allItemsWithSelected = selectedItemsRef.current.concat(
      allItems.filter((item) => !selectedSet.has(item.id)),
    );

    // const sorted = allItems.toSorted((a, b) => {
    //   const aSelected = props.selectedItems.includes(a.id);
    //   const bSelected = props.selectedItems.includes(b.id);

    //   if (aSelected === bSelected) return 0;
    //   return aSelected ? -1 : 1;
    // });

    return allItemsWithSelected;
  }, [allItems, props.displayFieldKey, props.selectedItems]);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayedItems.length,
    getScrollElement: () => parentRef.current,
    getItemKey: (i) => displayedItems[i].id,
    estimateSize: () => 45,
    overscan: 5,
  });

  const toggleItem = React.useCallback(
    (id: T["id"]) => {
      const next = props.selectedItems.includes(id)
        ? props.selectedItems.filter((i) => i !== id)
        : [...props.selectedItems, id];

      props.onChange(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.selectedItems, props.onChange],
  );

  const handleResetFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSearchTerm("");
    props.onChange(props.defaultValue ?? []);
  };

  const fetchItemsList = React.useCallback(
    async (search?: string) => {
      try {
        setIsLoading(true);
        const items = await props.fetchItems({
          search,
          onError: () => {
            toast({
              title: t("common.errors.fetch.title", { defaultValue: "Не удалось получить данные" }),
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          },
        });

        setAllItems(items);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.fetchItems, t, toast],
  );

  const setSearchDebounce = React.useMemo(() => {
    return debounce((search: string) => {
      if (!search.trim() && !fetchOnOpen) {
        setAllItems([]);
        return;
      }
      void fetchItemsList(search.trim());
    }, 500);
  }, [fetchItemsList, fetchOnOpen]);

  const onPopoverClose = React.useCallback(() => {
    onClose();
    setSearchDebounce.cancel();
    setSearchTerm("");
  }, [onClose, setSearchDebounce]);

  React.useEffect(() => {
    if (isOpen && fetchOnOpen) {
      void fetchItemsList(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchItemsList, fetchOnOpen]);

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverMaxHeight = useGetAvailableSpace(isOpen, triggerRef);

  return (
    <>
      <Popover
        placement="bottom-start"
        onOpen={onOpen}
        onClose={onPopoverClose}
        flip={true}
        initialFocusRef={initialFocusRef}
        isOpen={isOpen}
      >
        <PopoverTrigger>
          <Button
            isDisabled={props.isDisabled}
            size="sm"
            colorScheme="gray"
            variant="outline"
            rightIcon={
              <ChevronDownIcon style={isOpen ? { transform: "rotate(180deg)" } : undefined} />
            }
            borderColor={props.isError ? "red-error" : (props.btnBorderColor ?? undefined)}
            borderRadius={"12px"}
            ref={triggerRef}
            style={
              props.isError ? { boxShadow: "0 0 0 1px var(--chakra-colors-red-error)" } : undefined
            }
          >
            <Box
              display="inline-flex"
              alignItems="center"
              gap="5px"
              width={props.labelFullWidth ? "100%" : undefined}
            >
              <Text variant="text_sm_normal">{props.title}</Text>
              {props.selectedItems.length > 0 && (
                <Badge
                  borderRadius={"50px"}
                  height={"1.25rem"}
                  minWidth={"1.25rem"}
                  display={"inline-flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  {props.selectedItems.length}
                </Badge>
              )}
            </Box>
          </Button>
        </PopoverTrigger>
        <Portal>
          {isOpen && (
            <PopoverContent
              backgroundColor={"elevation-bg"}
              shadow={
                "0 1.5rem 1rem #00000008,0 .75rem .75rem #0000000a,0 .25rem .375rem #0000000d"
              }
            >
              <PopoverBody>
                {isLoading && (
                  <Flex
                    w="100%"
                    h="100%"
                    align="center"
                    justify="center"
                    zIndex={1}
                    position={"absolute"}
                    top={0}
                    left={0}
                  >
                    <SpinnerStyled zIndex={1} />
                    <Flex
                      w="100%"
                      h="100%"
                      bg={"whiteAlpha.500"}
                      _dark={{ bg: "whiteAlpha.300" }}
                      position="absolute"
                    ></Flex>
                  </Flex>
                )}
                <InputStyled
                  ref={initialFocusRef}
                  placeholder={t("common.search", { defaultValue: "Поиск" })}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSearchDebounce(e.target.value.trim());
                  }}
                  fullW
                  leftElement={
                    <InputLeftElement h="100%" w="auto" px="5px" color="gray-base-06">
                      <LoopIcon h={20} w={20} />
                    </InputLeftElement>
                  }
                />

                <Flex
                  direction={"column"}
                  marginTop={"0.5rem"}
                  maxH={`min(${popoverMaxHeight}, 60vh)`}
                  // maxH={`min(${"500px"}, 60vh)`}
                  // height={"500px"}
                  overflow={"auto"}
                  ref={parentRef}
                >
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                      <Flex
                        as="label"
                        key={virtualRow.index}
                        width="100%"
                        flex={1}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        gap={".5rem"}
                        padding={".3125rem .5rem"}
                        cursor={"pointer"}
                        borderRadius={"8px"}
                        _hover={{ backgroundColor: "gray-base-03" }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          // height: `10px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <Flex direction="column">
                          {displayedItems[virtualRow.index].id != undefined && (
                            <Text variant="text_xxs__normal" color={"text-base-02"}>
                              {displayedItems[virtualRow.index].id}

                              {props.showDisplayId &&
                                displayedItems[virtualRow.index].displayId &&
                                ` | ${displayedItems[virtualRow.index].displayId}`}
                            </Text>
                          )}
                          <Text
                            variant="text_sm_normal"
                            title={String(displayedItems[virtualRow.index][props.displayFieldKey])}
                          >
                            {truncate(
                              String(displayedItems[virtualRow.index][props.displayFieldKey]),
                              {
                                length: 30,
                              },
                            )}
                          </Text>
                        </Flex>
                        <Checkbox
                          colorScheme="brand"
                          value={displayedItems[virtualRow.index].id}
                          isChecked={props.selectedItems.includes(
                            displayedItems[virtualRow.index].id,
                          )}
                          onChange={() => {
                            selectedItemsRef.current.push(displayedItems[virtualRow.index]);
                            toggleItem(displayedItems[virtualRow.index].id);
                          }}
                        ></Checkbox>
                      </Flex>
                    ))}
                  </div>
                </Flex>

                {displayedItems.length !== 0 && props.selectedItems.length > 0 && <Divider />}

                {displayedItems.length === 0 && !isLoading && (
                  <Text m={2} variant="text_sm_normal">
                    {t("common.nothingFound", { defaultValue: "Ничего не найдено" })}
                  </Text>
                )}
                {(props.selectedItems.length > 0 || props.alwaysShowResetBtn) && !isLoading && (
                  <Button
                    w="100%"
                    size="sm"
                    variant="ghost"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleResetFilter(e)}
                    fontWeight={400}
                    _hover={{}}
                  >
                    {t("common.reset", { defaultValue: "Сбросить" })}
                  </Button>
                )}
              </PopoverBody>
            </PopoverContent>
          )}
        </Portal>
      </Popover>
    </>
  );
};
